import { Download, LoaderCircle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { apiClient, type ExportPage, type ExportRow } from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { Toast } from "@/shared/components/Toast";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatDateTime, formatInteger } from "@/shared/utils/format";
import {
  EXPORT_FORMAT_LABELS,
  EXPORT_KIND_LABELS,
  isExportActive,
  type ExportKind
} from "@/shared/vocabulary/exports";
import { downloadBlob } from "@/features/uploads/upload-shared";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import {
  canDownloadExport,
  canRetryExport,
  effectiveStatus,
  exportChips,
  exportLayoutNotes,
  exportSubject,
  formatFileSize,
  statusText,
  subjectPath
} from "./export-view";

const PAGE_SIZE = 20;

/** Cadência da página enquanto há pedido andando. */
export const DOWNLOADS_POLL_MS = 5000;

const KIND_TABS: Array<{ value: ExportKind | null; label: string }> = [
  { value: null, label: "Todos" },
  { value: "export_report", label: "Amostras" },
  { value: "export_draft", label: "Rascunhos" },
  { value: "export_upload_rows", label: "Planilhas de upload" },
  { value: "export_mentions", label: "Menções" }
];

function when(value: string | null): string | null {
  const parsed = parseServerTime(value);
  return parsed === null ? null : formatDateTime(new Date(parsed));
}

/**
 * Downloads: o histórico das planilhas pedidas à empresa (2026-10-15).
 *
 * É da EMPRESA — quem acessa vê e baixa qualquer item; "pedido por" é só
 * exibição. A lista se atualiza sozinha enquanto há pedido na fila ou
 * sendo gerado, e para quando todos terminam (`usePolling` com
 * `pausedUntil`, a regra do 429). Falha de AÇÃO (baixar, gerar de novo)
 * vira mensagem sem trocar a página.
 */
export function DownloadsPage({
  token,
  companyId,
  kind,
  highlightId,
  onKindChange
}: {
  token: string;
  companyId: string;
  kind: ExportKind | null;
  /** `?export=N`, vindo do sino: a linha ganha realce e rola até ela. */
  highlightId: number | null;
  onKindChange: (kind: ExportKind | null) => void;
}) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ExportPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState<Set<number>>(new Set());
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  // Descarte por CARGA: a resposta de uma página/aba antiga não vence a
  // da atual (mesma lição de menções e de `SearchSelect`).
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await apiClient.listExports(token, {
        page,
        size: PAGE_SIZE,
        ...(kind ? { kind } : {})
      });
      if (seq !== loadSeq.current) return;
      setData(result);
      setError(null);
    } catch (failure) {
      if (seq !== loadSeq.current) return;
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setError(errorMessage(failure, "Não foi possível carregar os downloads."));
    }
  }, [token, page, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = data?.items ?? [];
  const active = items.filter(item => isExportActive(item.status));
  usePolling({
    enabled: active.length > 0,
    onTick: () => void load(),
    signature: active.map(item => `${item.id}:${item.status}`).join(","),
    pausedUntil,
    intervalMs: DOWNLOADS_POLL_MS
  });

  // Realce: rola até a linha do sino uma vez por id, quando ela chega.
  const scrolledTo = useRef<number | null>(null);
  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlightId === null || scrolledTo.current === highlightId) return;
    if (!items.some(item => item.id === highlightId)) return;
    scrolledTo.current = highlightId;
    highlightRef.current?.scrollIntoView?.({ block: "center" });
  }, [highlightId, items]);

  const track = async (id: number, action: () => Promise<void>, fallback: string) => {
    setWorking(current => new Set(current).add(id));
    setActionError(null);
    try {
      await action();
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setActionError(errorMessage(failure, fallback));
    } finally {
      setWorking(current => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const download = (row: ExportRow) =>
    track(
      row.id,
      async () => {
        const blob = await apiClient.downloadExport(token, row.id);
        downloadBlob(blob, row.file_name ?? `exportacao-${row.id}.${row.format}`);
      },
      "Não foi possível baixar o arquivo."
    );

  const again = (row: ExportRow) =>
    track(
      row.id,
      async () => {
        const created = await apiClient.retryExport(token, row.id);
        if (!isExportActive(created.status)) {
          setActionError(created.message || "Não foi possível gerar de novo.");
          return;
        }
        setNotice("Gerando a planilha de novo — você será avisada no sino.");
        // Volta pra primeira página: o pedido novo é o mais recente.
        if (page !== 1) setPage(1);
        else await load();
      },
      "Não foi possível gerar de novo."
    );

  return (
    <div className="uploads-page downloads-page">
      <section className="card upload-history-card">
        <div className="card-title">
          <h3>
            <Download size={17} />
            Downloads{data ? ` · ${formatInteger(data.total)}` : ""}
          </h3>
        </div>
        <p className="downloads-intro">
          As planilhas que a empresa pediu. Cada arquivo fica disponível por 7 dias; depois
          dele, “Gerar de novo” refaz o mesmo recorte com os dados de hoje.
        </p>

        <div className="mode-toggle downloads-tabs" role="tablist" aria-label="Tipo de planilha">
          {KIND_TABS.map(tab => (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={kind === tab.value}
              className={kind === tab.value ? "active" : ""}
              onClick={() => {
                setPage(1);
                setData(null);
                onKindChange(tab.value);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <ApiState kind="error" onRetry={() => void load()}>
            {error}
          </ApiState>
        )}
        {actionError && (
          <p className="form-error" role="alert">
            {actionError}
          </p>
        )}
        {data === null && !error && <ApiState kind="loading">Carregando…</ApiState>}

        {data !== null && items.length === 0 && (
          <div className="upload-history-empty">
            <Download size={26} />
            <strong>Nenhuma planilha pedida ainda</strong>
            <span>
              Em Amostras, use “Exportar planilha”; em Uploads, “Baixar planilha”. O pedido
              aparece aqui e o sino avisa quando o arquivo ficar pronto.
            </span>
          </div>
        )}

        {items.length > 0 && (
          <div className="downloads-table-wrap">
            <table className="ai-admin-table downloads-table">
              <thead>
                <tr>
                  <th>Planilha</th>
                  <th>Pedido</th>
                  <th>Situação</th>
                  <th>Arquivo</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {items.map(row => (
                  <DownloadRow
                    key={row.id}
                    row={row}
                    companyId={companyId}
                    highlighted={row.id === highlightId}
                    rowRef={row.id === highlightId ? highlightRef : undefined}
                    busy={working.has(row.id)}
                    onDownload={() => void download(row)}
                    onAgain={() => void again(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data !== null && (
          <Pager
            page={page}
            size={PAGE_SIZE}
            onPage={setPage}
            count={{ total: data.total }}
          />
        )}
      </section>
      {notice && <Toast message={notice} onClose={() => setNotice(null)} />}
    </div>
  );
}

function DownloadRow({
  row,
  companyId,
  highlighted,
  rowRef,
  busy,
  onDownload,
  onAgain
}: {
  row: ExportRow;
  companyId: string;
  highlighted: boolean;
  rowRef?: React.Ref<HTMLTableRowElement>;
  busy: boolean;
  onDownload: () => void;
  onAgain: () => void;
}) {
  const status = effectiveStatus(row);
  const chips = exportChips(row);
  const requestedAt = when(row.requested_at);
  const expiresAt = when(row.expires_at);
  const who = row.requested_by_name || row.requested_by;
  const subject = exportSubject(row, EXPORT_KIND_LABELS[row.kind]);

  return (
    <tr
      ref={rowRef}
      className={highlighted ? "downloads-row-highlight" : undefined}
      aria-current={highlighted ? "true" : undefined}
    >
      <td>
        <strong className="downloads-kind">{EXPORT_KIND_LABELS[row.kind]}</strong>
        <Link className="downloads-subject" to={subjectPath(row, companyId)}>
          {subject}
        </Link>
        <span className="ai-admin-caption">
          {[EXPORT_FORMAT_LABELS[row.format], ...exportLayoutNotes(row)].join(" · ")}
        </span>
        {chips.length > 0 && (
          <span className="downloads-chips">
            {chips.map(chip => (
              <span key={chip.key} className="report-criteria-chip">
                {chip.label}
              </span>
            ))}
          </span>
        )}
      </td>
      <td>
        <span>{who}</span>
        {requestedAt && <span className="ai-admin-caption">{requestedAt}</span>}
      </td>
      <td>
        <span className={`upload-status ${statusTone(status)}`}>
          {isExportActive(status) && <LoaderCircle className="spin" size={12} />}
          {statusText(row)}
        </span>
      </td>
      <td>
        {status === "FINISHED" ? (
          <>
            <span>{formatFileSize(row.file_size)}</span>
            {row.row_count !== null && (
              <span className="ai-admin-caption">{formatInteger(row.row_count)} linhas</span>
            )}
            {expiresAt && <span className="ai-admin-caption">Válida até {expiresAt}</span>}
          </>
        ) : (
          <span className="ai-admin-caption">—</span>
        )}
      </td>
      <td>
        {/* O flex fica num div: `display: flex` no próprio <td> tira a
            célula da tabela, e a borda da linha parava antes dos botões. */}
        <div className="downloads-actions">
        {canDownloadExport(row) && (
          <button
            type="button"
            className="primary-button"
            disabled={busy}
            onClick={onDownload}
            aria-label={`Baixar ${subject}`}
          >
            {busy ? <LoaderCircle className="spin" size={15} /> : <Download size={15} />}
            Baixar
          </button>
        )}
        {canRetryExport(row) && (
          <button
            type="button"
            className="secondary-button"
            disabled={busy}
            onClick={onAgain}
            aria-label={`Gerar de novo ${subject}`}
          >
            <RefreshCw size={15} />
            Gerar de novo
          </button>
        )}
        </div>
      </td>
    </tr>
  );
}

function statusTone(status: ReturnType<typeof effectiveStatus>): string {
  if (status === "FINISHED") return "success";
  if (status === "ERRORED") return "warning";
  return "";
}
